import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '~/shared/common/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-[#8A3DFF]/16 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border border-[#DCD8D7] bg-[#FEFDFB] shadow-sm hover:bg-accent hover:text-accent-foreground focus:border-[#8A3DFF]',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Revel Street specific variants (Figma parity)
        revel: 'bg-[#8a3dff] text-white hover:bg-[#7b35e3]',
        revelOutline: 'border border-[#8a3dff] text-[#8a3dff] bg-white hover:bg-[#8a3dff]/5',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        // Exact heights from Figma
        revel40: 'h-10 rounded-[8px] px-3.5',
        revel36: 'h-9 rounded-[12px] px-3',
        revel28: 'h-7 rounded-[8px] px-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, isLoading = false, disabled, children, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), isLoading && 'relative cursor-wait pointer-events-none opacity-50', className)}
      ref={ref}
      aria-busy={isLoading || undefined}
      aria-disabled={disabled || isLoading || undefined}
      disabled={!asChild ? disabled || isLoading : undefined}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="inline-block size-4 rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden="true" />
          <span className="sr-only">Loading</span>
        </span>
      )}
      {isLoading ? <span className="inline-flex items-center gap-2 invisible">{children}</span> : children}
    </Comp>
  )
})
Button.displayName = 'Button'

export { Button, buttonVariants }
